import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, makeStyles } from '@material-ui/core';
import { LeaderboardPanel } from 'screens/TeamHome/components/LeaderboardPanel';
import { Banner } from 'components/Banner';
import { TeamsPanel } from './components/TeamsPanel';
import { OptionsAccordion } from './components/OptionsAccordion';
import { RootStore } from 'redux/store';
import { getUsers } from 'redux/thunks';
import { getPaginatedPosts } from 'redux/thunks/team';
import { CreditsModal } from 'components/Modals';
import { selectTeam, toggleFavoriteTeam } from 'redux/thunks';
import { Header } from 'components/Header';
import './TeamHome.scss';
import TeamHomeRouteSwitch from './router/TeamHomeRouteSwitch';
import { useParams } from 'react-router-dom';
import { FeedbackModal } from 'components/Modals/FeedbackModal';
import { MasterEggHunterModal } from 'components/Modals/MasterEggHunterModal';
import { EulaModal } from 'components/Modals/EulaModal';
import { TeamHomeTour } from 'components/Tours';
import { withRouteInformation } from 'components/withRouteInformation';
import { EasterEggModal } from 'components/Modals/EasterEggModal';
import { clearAmIMasterEggHunter } from 'redux/actions';

const useStyles = makeStyles({
	userRoot: {
		boxShadow: '0px 16px 24px #00000033',
		width: '304px',
		border: 'none',
	},
	leaderboardRoot: {
		top: '56px',
		position: 'absolute',
	},
});

interface TeamHomeProps {
	pathname: string;
}

const TeamHome: React.FC<TeamHomeProps> = (props: TeamHomeProps) => {
	const { pathname } = props;
	let { teamId } = useParams<{ teamId: string }>();
	const classes = useStyles();
	const dispatch = useDispatch();
	const userState = useSelector((state: RootStore) => state.user);
	const amIMasterEggHunter = useSelector((state: RootStore) => state.EasterEgg.amIMasterEggHunter);
	const [showTeamPanel, setShowTeamPanel] = useState(true);
	const [showLeaderboardPanel, setShowLeaderboardPanel] = useState(true);
	const [showMobileView, setShowMobileView] = useState(false);
	const [creditsModalOpen, setCreditsModalOpen] = useState(false);
	const [easterEggModalOpen, setEasterEggModalOpen] = useState(false);
	const [masterEggHunterModalOpen, setMasterEggHunderModalOpen] = useState(amIMasterEggHunter);
	const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
	const [eulaModalOpen, setEulaModalOpen] = useState(false);
	const [selectedTeamId, setSelectedTeam] = useState('');
	const [isTourOpen, setIsTourOpen] = useState(false);

	useEffect(() => {
		if (teamId && teamId !== userState.selectedTeam?.teamId) {
			dispatch(selectTeam(teamId));
		}

		if (userState.selectedTeam && userState.selectedTeam?.teamId !== selectedTeamId) {
			dispatch(getUsers(userState.selectedTeam.teamId));
			dispatch(getPaginatedPosts(userState.selectedTeam.teamId));
			setSelectedTeam(userState.selectedTeam?.teamId);
		}

		setMasterEggHunderModalOpen(amIMasterEggHunter);
	}, [dispatch, userState.selectedTeam, selectedTeamId, teamId, amIMasterEggHunter]);

	useEffect(() => {
		function handleResize(e: any) {
			setShowMobileView(e.matches);
		}

		window.innerWidth <= 1366 && setShowMobileView(true);

		window.matchMedia('(max-width: 1366px)').addListener(e => handleResize(e));
	}, []);

	// Use effect for managing if the tour should show or not
	useEffect(() => {
		if (userState.userId && pathname.includes('/all') && !userState.tours.includes('TeamHomeTour')) {
			setTimeout(function () {
				setIsTourOpen(true);
			}, 700);
		}
	}, [userState.tours, userState.userId, pathname]);

	useEffect(() => {
		if (showMobileView) {
			setShowTeamPanel(false);
			setShowLeaderboardPanel(false);
		} else {
			setShowTeamPanel(true);
			setShowLeaderboardPanel(true);
		}
	}, [showMobileView]);

	const handleTeamPanelButtonClick = () => {
		!showTeamPanel && setShowLeaderboardPanel(false);
		setShowTeamPanel(!showTeamPanel);
	};

	const handleTeamPanelClose = () => {
		setShowTeamPanel(false);
	};

	const handleLeaderboardPanelButtonClick = () => {
		!showLeaderboardPanel && setShowTeamPanel(false);
		setShowLeaderboardPanel(!showLeaderboardPanel);
	};

	const handleLeaderboardPanelClose = () => {
		setShowLeaderboardPanel(false);
	};

	const updateSelectedTeam = (teamId: string) => {
		dispatch(selectTeam(teamId));
	};

	const toggleMyFavoriteTeams = (teamId: string) => {
		dispatch(toggleFavoriteTeam(teamId));
	};

	const handleCloseEggHunterModalClick = () => {
		dispatch(clearAmIMasterEggHunter());
	};

	return (
		<>
			<Header
				userName={userState.userName}
				showMobileView={showMobileView}
				onEggHuntClicked={() => setEasterEggModalOpen(true)}
				onAboutClicked={() => setCreditsModalOpen(true)}
				onTermsClicked={() => setEulaModalOpen(true)}
				onTeamPanelButtonClick={handleTeamPanelButtonClick}
				onLeaderboardPanelButtonClick={handleLeaderboardPanelButtonClick}
			/>
			{/* Favorites & Teams Drawer */}
			<Drawer
				className='user-drawer'
				variant={showMobileView ? 'temporary' : 'persistent'}
				anchor='left'
				open={showTeamPanel}
				classes={{ paper: classes.userRoot }}
				onClose={handleTeamPanelClose}
			>
				<TeamsPanel
					teams={userState.teams}
					loading={userState.meLoading}
					selectedTeamId={userState.selectedTeam?.teamId}
					onTeamSelected={updateSelectedTeam}
					onToggleFavoriteTeam={toggleMyFavoriteTeams}
				/>
			</Drawer>

			{/* Main Banner */}
			<Banner />

			<div className='post-list-container'>
				<TeamHomeRouteSwitch />
			</div>
			<Drawer
				id='leaderboard-drawer-id'
				className='drawer'
				variant={showMobileView ? 'temporary' : 'persistent'}
				classes={{ root: classes.leaderboardRoot }}
				anchor='right'
				open={showLeaderboardPanel}
				onClose={handleLeaderboardPanelClose}
			>
				{!userState.selectedTeam?.hideLeaderboard && (
					<LeaderboardPanel
						teamUsers={userState.users}
						loading={userState.usersLoading}
						userId={userState.userId}
						teamId={userState.selectedTeam?.teamId || ''}
					/>
				)}
				<OptionsAccordion />

				<div className='page-credits'>
					<div onClick={() => setFeedbackModalOpen(true)}>Submit Feedback</div>
				</div>
			</Drawer>
			<CreditsModal open={creditsModalOpen} onCloseClicked={() => setCreditsModalOpen(false)} />
			<FeedbackModal open={feedbackModalOpen} onCloseClicked={() => setFeedbackModalOpen(false)} />
			<EasterEggModal open={easterEggModalOpen} onCloseClicked={() => setEasterEggModalOpen(false)} allowClickOutsideToClose={true} />
			<MasterEggHunterModal open={masterEggHunterModalOpen} onCloseClicked={() => handleCloseEggHunterModalClick()} />
			<EulaModal open={eulaModalOpen} openFromMenu={true} onCloseClicked={() => setEulaModalOpen(false)} />
			<TeamHomeTour open={isTourOpen} userName={userState.userName} onClose={() => setIsTourOpen(false)} />
		</>
	);
};

export default withRouteInformation(TeamHome);
